import {
  CarrierAddress,
  CarrierPackage,
  CompanyRequest,
  CompanyResponse,
  FileOwnerType,
  Identified,
  InventoryItemRequest,
  InventoryItemResponse,
  InventoryStatus,
  Permission,
  ProductFocus,
  ProjectPriority,
  ProjectRequest,
  ProjectResponse,
  ProjectStatus,
  ProjectTaskRequest,
  ProjectTaskResponse,
  ProjectTaskStatus,
  SampleShipmentRequest,
  SampleShipmentResponse
} from '../core/models';
import { formatDisplayEnum } from '../shared/display-format.pipe';
import { LookupSource } from '../core/lookup.service';

export type ResourceFieldType = 'text' | 'textarea' | 'select' | 'number' | 'money' | 'date' | 'checkbox' | 'email' | 'url';
export type ResourceFormValue = Record<string, unknown>;

export interface ResourceOption {
  label: string;
  value: string;
}

export interface ResourceColumn<TResponse extends Identified = Identified> {
  field: keyof TResponse & string;
  header: string;
  type?: 'text' | 'tag' | 'date' | 'money';
  lookup?: LookupSource;
}

export interface ResourceField {
  key: string;
  label: string;
  section?: string;
  type?: ResourceFieldType;
  required?: boolean;
  options?: ResourceOption[];
  lookup?: LookupSource;
  multiple?: boolean;
  placeholder?: string;
  fullWidth?: boolean;
  readonly?: boolean;
  defaultValue?: unknown;
  min?: number;
  max?: number;
  createOnly?: boolean;
}

export interface ResourcePageConfig<TResponse extends Identified = Identified, TRequest = ResourceFormValue> {
  title: string;
  eyebrow: string;
  description: string;
  endpoint: string;
  createLabel: string;
  permissions: Permission[];
  createPermission: Permission;
  updatePermissions: Permission[];
  deletePermissions?: Permission[];
  fileOwnerType?: FileOwnerType;
  dialogFileUpload?: {
    label: string;
    helperText?: string;
    accept?: string;
    multiple?: boolean;
  };
  detailLabel?: string;
  detailPath?: (row: TResponse) => string;
  detailLinkField?: keyof TResponse & string;
  columns: ResourceColumn<TResponse>[];
  fields: ResourceField[];
  createPath?: (formValue: ResourceFormValue) => string;
  updatePath?: (row: TResponse, formValue: ResourceFormValue) => string;
  toFormValue?: (row: TResponse) => ResourceFormValue;
  toRequest?: (formValue: ResourceFormValue) => TRequest;
}

const productFocusOptions = enumOptions<ProductFocus>([
  'BUBBLE_CHECK',
  'PREP_N_STICK',
  'CHAOS_GRIP',
  'SAFETY_GLASSES',
  'INTERNAL',
  'OTHER'
]);
const priorityOptions = enumOptions<ProjectPriority>(['HIGH', 'MEDIUM', 'LOW', 'PIPELINE', 'DROPPED']);
const projectStatusOptions = enumOptions<ProjectStatus>(['ON_TRACK', 'AT_RISK', 'BLOCKED', 'COMPLETED']);
const projectStageOptions = textOptions(['Discovery', 'Scoping', 'Pilot', 'Evaluation', 'Procurement', 'Implementation', 'Completed']);
const taskStatusOptions = enumOptions<ProjectTaskStatus>(['TODO', 'IN_PROGRESS', 'BLOCKED', 'COMPLETED']);
const weightUnitOptions = textOptions(['LB', 'KG']);
const dimensionUnitOptions = textOptions(['IN', 'CM']);
const inventoryStatusOptions = enumOptions<InventoryStatus>(['ACTIVE', 'DISCONTINUED']);
const inventoryCategoryOptions = textOptions(['Chemicals', 'Eyewear', 'PPE', 'Operations', 'Other']);
const inventoryUnitOptions = textOptions(['Each', 'Box', 'Case', 'Pair', 'Set', 'Pallet', 'Liter', 'Gallon', 'Kilogram', 'Pound']);

const defaultShipFromAddress: CarrierAddress = {
  contactName: 'Chau Nguyen',
  companyName: 'CHAOS Supplies',
  phone: '4808297888',
  email: 'cnguyen@chaossupplies.com',
  streetLines: ['3531 east indian school rd'],
  city: 'PHOENIX',
  stateOrProvinceCode: 'AZ',
  postalCode: '85018',
  countryCode: 'US',
  residential: false
};

export const companyResourceConfig: ResourcePageConfig<CompanyResponse, CompanyRequest> = {
  title: 'Companies',
  eyebrow: 'Accounts',
  description: 'Track business accounts, decision-makers, and relationship context in one place.',
  endpoint: '/companies',
  createLabel: 'New Company',
  permissions: ['COMPANY_READ', 'COMPANY_READ_ALL'],
  createPermission: 'COMPANY_CREATE',
  updatePermissions: ['COMPANY_UPDATE', 'COMPANY_UPDATE_ALL'],
  deletePermissions: ['COMPANY_DELETE'],
  fileOwnerType: 'COMPANY',
  detailLabel: 'Open',
  detailPath: (row) => `/companies/${row.id}`,
  detailLinkField: 'companyName',
  columns: [
    { field: 'companyName', header: 'Company' },
    { field: 'industry', header: 'Industry' },
    { field: 'relationship', header: 'Relationship', type: 'tag' },
    { field: 'potentialValue', header: 'Potential', type: 'money' },
    { field: 'contactPersonEmail', header: 'Contact Email' }
  ],
  fields: [
    { key: 'companyName', label: 'Company name', required: true },
    { key: 'industry', label: 'Industry', required: true },
    { key: 'relationship', label: 'Relationship', required: true, type: 'select', options: textOptions(['Prospect', 'Customer', 'Partner', 'Vendor']) },
    { key: 'potentialValue', label: 'Potential value', type: 'money', min: 0 },
    { key: 'websiteUrl', label: 'Website', type: 'url' },
    { key: 'contactPersonName', label: 'Customer name', required: true },
    { key: 'contactPersonEmail', label: 'Email address', type: 'email', required: true },
    { key: 'contactPersonTitle', label: 'Contact title' },
    { key: 'contactPersonPhone', label: 'Contact phone' },
    { key: 'contactPersonLinkedinUrl', label: 'LinkedIn URL', type: 'url', fullWidth: true },
    { key: 'shippingContactName', label: 'Shipping contact' },
    { key: 'shippingContactPhone', label: 'Shipping phone' },
    { key: 'shippingContactEmail', label: 'Shipping email', type: 'email' },
    { key: 'shippingStreetLine1', label: 'Shipping address 1', fullWidth: true },
    { key: 'shippingStreetLine2', label: 'Shipping address 2', fullWidth: true },
    { key: 'shippingCity', label: 'Shipping city' },
    { key: 'shippingStateOrProvinceCode', label: 'Shipping state' },
    { key: 'shippingPostalCode', label: 'Shipping postal code' },
    { key: 'shippingCountryCode', label: 'Shipping country' },
    { key: 'shippingResidential', label: 'Residential shipping address', type: 'checkbox', defaultValue: false, fullWidth: true }
  ],
  toRequest: (value) => companyRequestFromForm(value)
};

export const projectResourceConfig: ResourcePageConfig<ProjectResponse, ProjectRequest> = {
  title: 'Projects',
  eyebrow: 'Pipeline',
  description: 'Manage active projects, ownership, priority, and current status at a glance.',
  endpoint: '/projects',
  createLabel: 'New Project',
  permissions: ['PROJECT_READ', 'PROJECT_READ_ALL'],
  createPermission: 'PROJECT_CREATE',
  updatePermissions: ['PROJECT_UPDATE', 'PROJECT_UPDATE_ALL'],
  deletePermissions: ['PROJECT_DELETE'],
  fileOwnerType: 'PROJECT',
  detailLabel: 'Workspace',
  detailPath: (row) => `/projects/${row.id}`,
  detailLinkField: 'projectName',
  columns: [
    { field: 'projectName', header: 'Project' },
    { field: 'companyId', header: 'Company', lookup: 'companies' },
    { field: 'productFocus', header: 'Product focus' },
    { field: 'status', header: 'Status', type: 'tag' },
    { field: 'priority', header: 'Priority', type: 'tag' },
    { field: 'stage', header: 'Stage' },
    { field: 'estimatedValue', header: 'Estimated value', type: 'money' },
    { field: 'projectOwnerId', header: 'Owner', lookup: 'users' }
  ],
  fields: [
    { key: 'projectName', label: 'Project name', required: true },
    { key: 'companyId', label: 'Company', required: true, type: 'select', lookup: 'companies' },
    { key: 'productFocus', label: 'Product focus', type: 'select', options: productFocusOptions },
    { key: 'projectOwnerId', label: 'Project owner', required: true, type: 'select', lookup: 'workflowUsers' },
    { key: 'stage', label: 'Stage', type: 'select', options: projectStageOptions, defaultValue: 'Discovery' },
    { key: 'priority', label: 'Priority', type: 'select', options: priorityOptions, defaultValue: 'MEDIUM' },
    { key: 'status', label: 'Status', type: 'select', options: projectStatusOptions, defaultValue: 'ON_TRACK' },
    { key: 'estimatedValue', label: 'Estimated value', type: 'money', min: 0 },
    { key: 'probabilityPercent', label: 'Probability %', type: 'number', min: 0, max: 100 },
    { key: 'expectedCloseDate', label: 'Expected close', type: 'date' },
    { key: 'lastContactDate', label: 'Last contact', type: 'date' },
    { key: 'nextFollowUpDate', label: 'Next follow-up', type: 'date' },
    { key: 'allowedUserIds', label: 'Project team', type: 'select', lookup: 'workflowUsers', multiple: true, fullWidth: true },
    { key: 'publicProject', label: 'Visible to all active users', type: 'checkbox', defaultValue: false, fullWidth: true }
  ],
  toFormValue: (row) => ({
    ...row,
    expectedCloseDate: toDateInput(row.expectedCloseDate),
    lastContactDate: toDateInput(row.lastContactDate),
    nextFollowUpDate: toDateInput(row.nextFollowUpDate),
    allowedUserIds: row.allowedUserIds ?? []
  }),
  toRequest: (value) =>
    compact({
      projectName: value['projectName'],
      companyId: value['companyId'],
      productFocus: value['productFocus'],
      projectOwnerId: value['projectOwnerId'],
      stage: value['stage'],
      priority: value['priority'],
      status: value['status'],
      estimatedValue: toNumber(value['estimatedValue']),
      probabilityPercent: toNumber(value['probabilityPercent']),
      expectedCloseDate: toApiDate(value['expectedCloseDate']),
      lastContactDate: toApiDate(value['lastContactDate']),
      nextFollowUpDate: toApiDate(value['nextFollowUpDate']),
      publicProject: Boolean(value['publicProject']),
      allowedUserIds: splitList(value['allowedUserIds'])
    }) as ProjectRequest
};

export const projectTaskResourceConfig: ResourcePageConfig<ProjectTaskResponse, ProjectTaskRequest> = {
  title: 'Project Tasks',
  eyebrow: 'Execution',
  description: 'Track assigned work, due dates, blockers, and project progress.',
  endpoint: '/project-tasks',
  createLabel: 'New Task',
  permissions: ['PROJECT_TASK_READ', 'PROJECT_TASK_READ_ALL'],
  createPermission: 'PROJECT_TASK_CREATE',
  updatePermissions: ['PROJECT_TASK_UPDATE', 'PROJECT_TASK_UPDATE_ALL'],
  deletePermissions: ['PROJECT_TASK_DELETE'],
  fileOwnerType: 'PROJECT_TASK',
  detailLabel: 'Open',
  detailPath: (row) => `/tasks/${row.id}`,
  detailLinkField: 'title',
  createPath: (value) => `/project-tasks/project/${value['projectId']}`,
  columns: [
    { field: 'title', header: 'Task' },
    { field: 'projectId', header: 'Project', lookup: 'projects' },
    { field: 'status', header: 'Status', type: 'tag' },
    { field: 'priority', header: 'Priority', type: 'tag' },
    { field: 'assignedUserId', header: 'Assigned To', lookup: 'users' },
    { field: 'dueDate', header: 'Due Date', type: 'date' }
  ],
  fields: [
    { key: 'projectId', label: 'Project', required: true, type: 'select', lookup: 'projects' },
    { key: 'assignedUserId', label: 'Assigned user', type: 'select', lookup: 'workflowUsers' },
    { key: 'title', label: 'Title', required: true },
    { key: 'priority', label: 'Priority', type: 'select', options: priorityOptions, defaultValue: 'MEDIUM' },
    { key: 'status', label: 'Status', type: 'select', options: taskStatusOptions, defaultValue: 'TODO' },
    { key: 'dueDate', label: 'Due date', type: 'date' },
    { key: 'description', label: 'Description', type: 'textarea', fullWidth: true }
  ],
  toFormValue: (row) => ({
    ...row,
    dueDate: toDateInput(row.dueDate)
  }),
  toRequest: (value) =>
    compact({
      assignedUserId: value['assignedUserId'],
      title: value['title'],
      description: value['description'],
      priority: value['priority'],
      status: value['status'],
      dueDate: toApiDate(value['dueDate'])
    }) as ProjectTaskRequest
};

export const sampleShipmentResourceConfig: ResourcePageConfig<SampleShipmentResponse, SampleShipmentRequest> = {
  title: 'Sample Shipments',
  eyebrow: 'Logistics',
  description: 'Monitor sample shipments, tracking, delivery status, and related project links.',
  endpoint: '/sample-shipments',
  createLabel: 'New Shipment',
  permissions: ['SAMPLE_SHIPMENT_READ', 'SAMPLE_SHIPMENT_READ_ALL'],
  createPermission: 'SAMPLE_SHIPMENT_CREATE',
  updatePermissions: ['SAMPLE_SHIPMENT_UPDATE', 'SAMPLE_SHIPMENT_UPDATE_ALL'],
  deletePermissions: ['SAMPLE_SHIPMENT_DELETE'],
  fileOwnerType: 'SAMPLE_SHIPMENT',
  detailLabel: 'Open',
  detailPath: (row) => `/sample-shipments/${row.id}`,
  detailLinkField: 'trackingNumber',
  columns: [
    { field: 'trackingNumber', header: 'Tracking' },
    { field: 'projectId', header: 'Project', lookup: 'projects' },
    { field: 'status', header: 'Status', type: 'tag' },
    { field: 'carrier', header: 'Carrier' },
    { field: 'recipientContactName', header: 'Recipient' },
    { field: 'expectedDeliveryDate', header: 'Expected', type: 'date' }
  ],
  fields: [
    { key: 'projectId', label: 'Project', required: true, type: 'select', lookup: 'projects', section: 'Shipment Info' },
    { key: 'inventoryItemId', label: 'Inventory item', type: 'select', lookup: 'inventoryItems', required: true, section: 'Shipment Info' },
    { key: 'productQuantity', label: 'Quantity', type: 'number', required: true, min: 1, section: 'Shipment Info' },
    { key: 'notes', label: 'Shipment notes', type: 'textarea', fullWidth: true, section: 'Shipment Info' },
    { key: 'packageWeight', label: 'Package weight', type: 'number', min: 0.01, required: true, section: 'Package' },
    { key: 'packageWeightUnit', label: 'Weight unit', type: 'select', options: weightUnitOptions, defaultValue: 'LB', required: true, section: 'Package' },
    { key: 'packageLength', label: 'Package length', type: 'number', min: 1, section: 'Package' },
    { key: 'packageWidth', label: 'Package width', type: 'number', min: 1, section: 'Package' },
    { key: 'packageHeight', label: 'Package height', type: 'number', min: 1, section: 'Package' },
    { key: 'packageDimensionUnit', label: 'Dimension unit', type: 'select', options: dimensionUnitOptions, defaultValue: 'IN', section: 'Package' },
    { key: 'recipientContactName', label: 'Recipient name', required: true, section: 'Ship To' },
    { key: 'recipientEmail', label: 'Recipient email', type: 'email', section: 'Ship To' },
    { key: 'shipToCompanyName', label: 'Ship to company', readonly: true, section: 'Ship To' },
    { key: 'shipToPhone', label: 'Ship to phone', required: true, section: 'Ship To' },
    { key: 'shipToStreetLine1', label: 'Ship to address 1', required: true, fullWidth: true, section: 'Ship To' },
    { key: 'shipToStreetLine2', label: 'Ship to address 2', fullWidth: true, section: 'Ship To' },
    { key: 'shipToCity', label: 'Ship to city', required: true, section: 'Ship To' },
    { key: 'shipToStateOrProvinceCode', label: 'Ship to state', required: true, section: 'Ship To' },
    { key: 'shipToPostalCode', label: 'Ship to postal code', required: true, section: 'Ship To' },
    { key: 'shipToCountryCode', label: 'Ship to country', required: true, defaultValue: 'US', section: 'Ship To' },
    { key: 'shipToResidential', label: 'Residential delivery address', type: 'checkbox', defaultValue: false, fullWidth: true, section: 'Ship To' },
    { key: 'shipFromContactName', label: 'Ship from contact', required: true, defaultValue: defaultShipFromAddress.contactName, placeholder: defaultShipFromAddress.contactName, section: 'Ship From' },
    { key: 'shipFromCompanyName', label: 'Ship from company', defaultValue: defaultShipFromAddress.companyName, placeholder: defaultShipFromAddress.companyName, section: 'Ship From' },
    { key: 'shipFromPhone', label: 'Ship from phone', required: true, defaultValue: defaultShipFromAddress.phone, placeholder: defaultShipFromAddress.phone, section: 'Ship From' },
    { key: 'shipFromEmail', label: 'Ship from email', type: 'email', defaultValue: defaultShipFromAddress.email, placeholder: defaultShipFromAddress.email, section: 'Ship From' },
    { key: 'shipFromStreetLine1', label: 'Ship from address 1', required: true, defaultValue: defaultShipFromAddress.streetLines?.[0], placeholder: defaultShipFromAddress.streetLines?.[0], fullWidth: true, section: 'Ship From' },
    { key: 'shipFromStreetLine2', label: 'Ship from address 2', fullWidth: true, section: 'Ship From' },
    { key: 'shipFromCity', label: 'Ship from city', required: true, defaultValue: defaultShipFromAddress.city, placeholder: defaultShipFromAddress.city, section: 'Ship From' },
    { key: 'shipFromStateOrProvinceCode', label: 'Ship from state', required: true, defaultValue: defaultShipFromAddress.stateOrProvinceCode, placeholder: defaultShipFromAddress.stateOrProvinceCode, section: 'Ship From' },
    { key: 'shipFromPostalCode', label: 'Ship from postal code', required: true, defaultValue: defaultShipFromAddress.postalCode, placeholder: defaultShipFromAddress.postalCode, section: 'Ship From' },
    { key: 'shipFromCountryCode', label: 'Ship from country', required: true, defaultValue: defaultShipFromAddress.countryCode, placeholder: defaultShipFromAddress.countryCode, section: 'Ship From' }
  ],
  toFormValue: (row) => {
    const product = row.products?.[0];
    return {
      ...addressFormValue(row.shipFromAddress ?? defaultShipFromAddress, 'shipFrom'),
      ...addressFormValue(row.shipToAddress, 'shipTo'),
      ...packageFormValue(row.packageDetails),
      projectId: row.projectId ?? '',
      recipientContactName: row.recipientContactName ?? '',
      recipientEmail: row.recipientEmail ?? '',
      inventoryItemId: product?.inventoryItemId ?? '',
      productQuantity: product?.quantity ?? '',
      notes: row.notes ?? ''
    };
  },
  toRequest: (value) => sampleShipmentRequestFromForm(value)
};

export const inventoryResourceConfig: ResourcePageConfig<InventoryItemResponse, InventoryItemRequest> = {
  title: 'Inventory',
  eyebrow: 'Operations',
  description: 'Track items, locations, reorder levels, availability, and inventory movement history.',
  endpoint: '/inventory-items',
  createLabel: 'New Inventory Item',
  permissions: ['INVENTORY_READ', 'INVENTORY_READ_ALL'],
  createPermission: 'INVENTORY_CREATE',
  updatePermissions: ['INVENTORY_UPDATE', 'INVENTORY_UPDATE_ALL'],
  deletePermissions: ['INVENTORY_DELETE'],
  fileOwnerType: 'INVENTORY',
  dialogFileUpload: {
    label: 'Item picture',
    helperText: 'Optional. Upload one product photo for quick visual identification.',
    accept: 'image/*'
  },
  detailLabel: 'Workspace',
  detailPath: (row) => `/inventory/${row.id}`,
  detailLinkField: 'itemName',
  columns: [
    { field: 'sku', header: 'SKU' },
    { field: 'itemName', header: 'Item' },
    { field: 'category', header: 'Category' },
    { field: 'status', header: 'Status', type: 'tag' },
    { field: 'quantityOnHand', header: 'On Hand' },
    { field: 'availableQuantity', header: 'Available' },
    { field: 'reorderLevel', header: 'Reorder At' },
    { field: 'location', header: 'Location' }
  ],
  fields: [
    { key: 'sku', label: 'SKU', required: true },
    { key: 'itemName', label: 'Item name', required: true },
    { key: 'category', label: 'Category', type: 'select', options: inventoryCategoryOptions, required: true },
    { key: 'unitOfMeasure', label: 'Unit', type: 'select', options: inventoryUnitOptions, required: true },
    { key: 'location', label: 'Location', required: true },
    { key: 'supplierCompanyId', label: 'Supplier', type: 'select', lookup: 'companies' },
    { key: 'quantityOnHand', label: 'Initial on hand', type: 'number', min: 0, defaultValue: 0, createOnly: true, required: true },
    { key: 'reservedQuantity', label: 'Initially reserved', type: 'number', min: 0, defaultValue: 0, createOnly: true },
    { key: 'reorderLevel', label: 'Reorder level', type: 'number', min: 0, required: true },
    { key: 'status', label: 'Lifecycle status', type: 'select', options: inventoryStatusOptions, defaultValue: 'ACTIVE' },
    { key: 'description', label: 'Description', type: 'textarea', fullWidth: true },
    { key: 'notes', label: 'Notes', type: 'textarea', fullWidth: true }
  ],
  toFormValue: (row) => ({
    ...row,
    status: row.status === 'DISCONTINUED' ? 'DISCONTINUED' : 'ACTIVE'
  }),
  toRequest: (value) => inventoryRequestFromForm(value)
};

function companyRequestFromForm(value: ResourceFormValue): CompanyRequest {
  return compact({
    companyName: value['companyName'],
    industry: value['industry'],
    relationship: value['relationship'],
    potentialValue: value['potentialValue'],
    websiteUrl: value['websiteUrl'],
    contactPersonName: value['contactPersonName'],
    contactPersonEmail: value['contactPersonEmail'],
    contactPersonTitle: value['contactPersonTitle'],
    contactPersonPhone: value['contactPersonPhone'],
    contactPersonLinkedinUrl: value['contactPersonLinkedinUrl'],
    ...shippingAddressRequestFields(value)
  }) as CompanyRequest;
}

function shippingAddressRequestFields(value: ResourceFormValue): ResourceFormValue {
  const hasShippingValue = [
    'shippingContactName',
    'shippingContactPhone',
    'shippingContactEmail',
    'shippingStreetLine1',
    'shippingStreetLine2',
    'shippingCity',
    'shippingStateOrProvinceCode',
    'shippingPostalCode',
    'shippingCountryCode'
  ].some((key) => stringValue(value[key]));

  if (!hasShippingValue) {
    return {};
  }

  return {
    shippingContactName: value['shippingContactName'],
    shippingContactPhone: value['shippingContactPhone'],
    shippingContactEmail: value['shippingContactEmail'],
    shippingStreetLine1: value['shippingStreetLine1'],
    shippingStreetLine2: value['shippingStreetLine2'],
    shippingCity: value['shippingCity'],
    shippingStateOrProvinceCode: value['shippingStateOrProvinceCode'],
    shippingPostalCode: value['shippingPostalCode'],
    shippingCountryCode: value['shippingCountryCode'] || 'US',
    shippingResidential: Boolean(value['shippingResidential'])
  };
}

function inventoryRequestFromForm(value: ResourceFormValue): InventoryItemRequest {
  return compact({
    sku: value['sku'],
    itemName: value['itemName'],
    category: value['category'],
    description: value['description'],
    unitOfMeasure: value['unitOfMeasure'],
    location: value['location'],
    supplierCompanyId: value['supplierCompanyId'],
    quantityOnHand: toNumber(value['quantityOnHand']),
    reservedQuantity: toNumber(value['reservedQuantity']),
    reorderLevel: toNumber(value['reorderLevel']),
    status: value['status'],
    notes: value['notes']
  }) as InventoryItemRequest;
}

function sampleShipmentRequestFromForm(value: ResourceFormValue): SampleShipmentRequest {
  const product = compact({
    inventoryItemId: value['inventoryItemId'],
    quantity: toNumber(value['productQuantity'])
  });

  return compact({
    projectId: value['projectId'],
    recipientContactName: value['recipientContactName'],
    recipientEmail: value['recipientEmail'],
    products: Object.keys(product).length > 0 ? [product] : undefined,
    shipFromAddress: buildAddress(value, 'shipFrom'),
    shipToAddress: buildAddress(value, 'shipTo', {
      contactName: value['recipientContactName'],
      email: value['recipientEmail']
    }),
    packageDetails: buildPackage(value),
    notes: value['notes']
  }) as SampleShipmentRequest;
}

function packageFormValue(carrierPackage: CarrierPackage | undefined): ResourceFormValue {
  return {
    packageWeight: carrierPackage?.weight ?? '',
    packageWeightUnit: carrierPackage?.weightUnit ?? 'LB',
    packageLength: carrierPackage?.length ?? '',
    packageWidth: carrierPackage?.width ?? '',
    packageHeight: carrierPackage?.height ?? '',
    packageDimensionUnit: carrierPackage?.dimensionUnit ?? 'IN'
  };
}

function buildPackage(value: ResourceFormValue): CarrierPackage | undefined {
  const packageDetails = compact({
    weight: toNumber(value['packageWeight']),
    weightUnit: value['packageWeightUnit'] || 'LB',
    length: toNumber(value['packageLength']),
    width: toNumber(value['packageWidth']),
    height: toNumber(value['packageHeight']),
    dimensionUnit: value['packageDimensionUnit'] || 'IN'
  });
  return Object.keys(packageDetails).length > 0 ? packageDetails as CarrierPackage : undefined;
}

function addressFormValue(address: CarrierAddress | undefined, prefix: string): ResourceFormValue {
  return {
    [`${prefix}ContactName`]: address?.contactName ?? '',
    [`${prefix}CompanyName`]: address?.companyName ?? '',
    [`${prefix}Phone`]: address?.phone ?? '',
    [`${prefix}Email`]: address?.email ?? '',
    [`${prefix}StreetLine1`]: address?.streetLines?.[0] ?? '',
    [`${prefix}StreetLine2`]: address?.streetLines?.[1] ?? '',
    [`${prefix}City`]: address?.city ?? '',
    [`${prefix}StateOrProvinceCode`]: address?.stateOrProvinceCode ?? '',
    [`${prefix}PostalCode`]: address?.postalCode ?? '',
    [`${prefix}CountryCode`]: address?.countryCode ?? 'US',
    [`${prefix}Residential`]: address?.residential ?? false
  };
}

function buildAddress(
  value: ResourceFormValue,
  prefix: string,
  overrides: ResourceFormValue = {}
): CarrierAddress | undefined {
  if (!hasAddressValue(value, prefix)) {
    return undefined;
  }
  const streetLines = [
    value[`${prefix}StreetLine1`],
    value[`${prefix}StreetLine2`]
  ]
    .map((entry) => stringValue(entry))
    .filter(Boolean) as string[];

  const address = compact({
    contactName: overrides['contactName'] ?? value[`${prefix}ContactName`],
    companyName: value[`${prefix}CompanyName`],
    phone: value[`${prefix}Phone`],
    email: overrides['email'] ?? value[`${prefix}Email`],
    streetLines: streetLines.length > 0 ? streetLines : undefined,
    city: value[`${prefix}City`],
    stateOrProvinceCode: value[`${prefix}StateOrProvinceCode`],
    postalCode: value[`${prefix}PostalCode`],
    countryCode: value[`${prefix}CountryCode`] || 'US',
    residential: Boolean(value[`${prefix}Residential`])
  });
  return Object.keys(address).length > 0 ? address as CarrierAddress : undefined;
}

function hasAddressValue(value: ResourceFormValue, prefix: string): boolean {
  return [
    `${prefix}ContactName`,
    `${prefix}CompanyName`,
    `${prefix}Phone`,
    `${prefix}Email`,
    `${prefix}StreetLine1`,
    `${prefix}StreetLine2`,
    `${prefix}City`,
    `${prefix}StateOrProvinceCode`,
    `${prefix}PostalCode`
  ].some((key) => stringValue(value[key]));
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
}

function enumOptions<T extends string>(values: T[]): ResourceOption[] {
  return values.map((value) => ({ label: formatEnum(value), value }));
}

function textOptions(values: string[]): ResourceOption[] {
  return values.map((value) => ({ label: value, value }));
}

export function formatEnum(value: string): string {
  return formatDisplayEnum(value);
}

function compact(value: ResourceFormValue): ResourceFormValue {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined && entry !== null && entry !== '')
  );
}

function toNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function splitList(value: unknown): string[] | undefined {
  if (Array.isArray(value)) {
    const entries = value.map(String).filter(Boolean);
    return entries.length > 0 ? entries : undefined;
  }

  if (typeof value !== 'string' || value.trim() === '') {
    return undefined;
  }

  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function toApiDate(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.trim() === '') {
    return undefined;
  }

  if (value.includes('/')) {
    return value;
  }

  const [year, month, day] = value.split('-');
  if (!year || !month || !day) {
    return undefined;
  }

  return `${month}/${day}/${year}`;
}

function toDateInput(value: unknown): string {
  if (typeof value !== 'string' || value.trim() === '') {
    return '';
  }

  if (value.includes('-')) {
    return value.slice(0, 10);
  }

  const [month, day, year] = value.split('/');
  if (!year || !month || !day) {
    return '';
  }

  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}
